/**
 * Pure implementation of the cross-spot week certification gate. Import it through
 * `cross-spot-week-gate.ts` (server-only) everywhere except the weekly rankings writer, which is
 * loaded by test suites that run outside the react-server condition. No client bundle imports
 * either module: the gate reaches client components only as a server-computed prop.
 */
import {
  DEFAULT_REVISION_MODE,
  SPORT_REVISIONS_FLAG,
  SPORT_SHADOW_REVISIONS_FLAG,
  parseRevisionMode,
  publishRevisionFor,
  type RevisionMode,
} from "./engines/core/revision-mode";
import { REVISION_TWO } from "./engines/core/revisions";
import { isEngineSport } from "./engines/core/sports";

export const CROSS_SPOT_WEEK_FLAG = "CONDITIONS_CROSS_SPOT_WEEK_CERTIFIED_SPORTS" as const;
/** Sports whose cross-spot week ranking is withheld until certified. */
export const CROSS_SPOT_WEEK_REQUIRES_CERTIFICATION: readonly string[] = Object.freeze(["surf"]);
/** The reason stored on withheld rows; unchanged so existing rows stay readable. */
export const CROSS_SPOT_WEEK_UNCERTIFIED_REASON = "surf-semantics-not-certified" as const;

export type CrossSpotWeekGateReason =
  | "not-required" | "certified" | "flag-missing" | "flag-invalid" | "revision-below-2";
export type CrossSpotWeekGate = Readonly<{ sport: string; certified: boolean; reason: CrossSpotWeekGateReason }>;

type GateEnv = Readonly<Record<string, string | undefined>>;

/**
 * The grammar of engines/core/flags.ts parseSportList, restated here because page readers and
 * middleware may import only the engine declaration modules (engine-importers.test.ts): exact
 * non-ski sport ids, whitespace around entries ignored, empty entries skipped, and any
 * unattributable entry makes the whole list invalid (fail closed).
 */
function listedSports(raw: string | undefined): Readonly<{ sports: readonly string[]; invalid: boolean }> {
  const entries = (raw ?? "").split(",").map((entry) => entry.trim()).filter((entry) => entry.length > 0);
  const invalid = entries.some((entry) => !isEngineSport(entry));
  return { sports: invalid ? [] : entries, invalid };
}

/** The revision mode exactly as engines/core/flags.ts parseEngineFlags reads it from env. */
function revisionModeOf(env: GateEnv): RevisionMode {
  const publish = env[SPORT_REVISIONS_FLAG];
  const shadow = env[SPORT_SHADOW_REVISIONS_FLAG];
  return !publish && !shadow ? DEFAULT_REVISION_MODE : parseRevisionMode({ publish, shadow });
}

function gate(sport: string, certified: boolean, reason: CrossSpotWeekGateReason): CrossSpotWeekGate {
  return Object.freeze({ sport, certified, reason });
}

export function crossSpotWeekGate(sport: string, env: GateEnv = process.env): CrossSpotWeekGate {
  if (!CROSS_SPOT_WEEK_REQUIRES_CERTIFICATION.includes(sport)) return gate(sport, true, "not-required");
  const listed = listedSports(env[CROSS_SPOT_WEEK_FLAG]);
  if (listed.invalid) return gate(sport, false, "flag-invalid");
  if (!isEngineSport(sport) || !listed.sports.includes(sport)) return gate(sport, false, "flag-missing");
  const revision = publishRevisionFor(revisionModeOf(env), sport);
  if (revision < REVISION_TWO) return gate(sport, false, "revision-below-2");
  return gate(sport, true, "certified");
}

export function crossSpotWeekCertified(sport: string, env: GateEnv = process.env): boolean {
  return crossSpotWeekGate(sport, env).certified;
}
